import { Box, ButtonBase, CircularProgress, Stack, Typography } from '@mui/material';
import type { FallbackRender } from '@sentry/react';
import { useState } from 'react';

import { resolveSupportReference } from '../../initialisers/sentryReporting';
import { isLeavingPage } from '../../lib/navigation/pageExit';
import { Iconify } from '../iconify';

// Support address shown on the error screen; matches the one used on generated invoices/PDFs.
const SUPPORT_EMAIL = 'support@getaltitude.ai';

const FONT_FAMILY = '"Helvetica Neue", "Inter Variable", "Open Sans", Helvetica, Arial, sans-serif';

// Per-mode colour tokens taken straight from the Error Page design (dark + light), applied inline
// because the fallback renders without the theme (see resolveIsDark).
const PALETTE = {
  dark: {
    pageBg: '#0b0b0c',
    iconBg: 'rgba(224,52,52,0.12)',
    iconBorder: 'rgba(224,52,52,0.28)',
    iconColor: '#f2726f',
    title: '#fafafa',
    body: '#a1a1aa',
    pillBg: 'rgba(255,255,255,0.04)',
    pillBorder: 'rgba(255,255,255,0.08)',
    refLabel: '#6b6b73',
    refValue: '#d4d4d8',
    copyColor: '#8a8a93',
    copyHoverBg: 'rgba(255,255,255,0.08)',
    copyHoverColor: '#e4e4e7',
    reloadBg: 'transparent',
    reloadColor: '#e4e4e7',
    reloadBorder: 'rgba(255,255,255,0.15)',
    reloadHoverBg: 'rgba(255,255,255,0.06)',
    reloadHoverBorder: 'rgba(255,255,255,0.22)',
    linkColor: '#8a8a93',
    linkHoverColor: '#c4c4ca',
    homeBg: '#ededed',
    homeColor: '#18181b',
    homeHoverBg: '#ffffff',
  },
  light: {
    pageBg: '#fcfbfa',
    iconBg: 'rgba(192,57,43,0.09)',
    iconBorder: 'rgba(192,57,43,0.2)',
    iconColor: '#c0392b',
    title: '#37352f',
    body: '#6b6a64',
    pillBg: '#f4f3ef',
    pillBorder: '#e6e4de',
    refLabel: '#a8a399',
    refValue: '#403d37',
    copyColor: '#9a9892',
    copyHoverBg: '#e6e4de',
    copyHoverColor: '#403d37',
    reloadBg: '#ffffff',
    reloadColor: '#514e47',
    reloadBorder: '#e2e0da',
    reloadHoverBg: '#fcfbfa',
    reloadHoverBorder: '#dcdad3',
    linkColor: '#9a9892',
    linkHoverColor: '#514e47',
    homeBg: '#37352f',
    homeColor: '#ffffff',
    homeHoverBg: '#2b2925',
  },
} as const;

// The root ErrorBoundary renders this fallback ABOVE the ThemeProvider, so the MUI theme context is
// gone. Read the colour mode MUI persists in localStorage (falling back to the app's dark default) so
// the error screen still matches the user's current theme instead of snapping to MUI's default light.
function resolveIsDark(): boolean {
  try {
    const stored = window.localStorage.getItem('theme-mode');
    if (stored === 'light') {
      return false;
    }
    if (stored === 'dark') {
      return true;
    }
    if (stored === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
  } catch {
    // localStorage/matchMedia unavailable — fall through to the app default.
  }
  return true;
}

// Friendly "Something went wrong" screen shown when a page crashes mid-render, so the user gets a clear
// recovery path instead of a blank page. Router-independent (window.location / mailto) because the root
// boundary sits above the Router; the Sentry eventId doubles as the support reference.
export const ErrorFallbackPage: FallbackRender = ({ eventId }) => {
  const [copied, setCopied] = useState(false);
  const colors = resolveIsDark() ? PALETTE.dark : PALETTE.light;
  // Only ever show a reference support can actually look up — see resolveSupportReference.
  const reference = resolveSupportReference(eventId);

  // A crash while the browser is already navigating away means an in-flight request (typically
  // the lazy chunk of a route we are about to abandon) got cancelled, not that anything is
  // wrong. Show the same spinner the app uses for any other transition until the next page
  // paints, keeping the page background so a dark-mode user doesn't get a white flash.
  if (isLeavingPage()) {
    return (
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: colors.pageBg,
        }}
      >
        <CircularProgress sx={{ color: colors.body }} />
      </Box>
    );
  }

  const copyReference = () => {
    if (!reference) {
      return;
    }
    try {
      navigator.clipboard?.writeText(reference);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      // Clipboard blocked (insecure context / denied permission) — leave the icon unchanged.
    }
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        overflowY: 'auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 3,
        py: 10,
        bgcolor: colors.pageBg,
        fontFamily: FONT_FAMILY,
      }}
    >
      <Stack sx={{ alignItems: 'center', textAlign: 'center', maxWidth: 460 }}>
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: '18px',
            bgcolor: colors.iconBg,
            border: `1px solid ${colors.iconBorder}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: '26px',
          }}
        >
          <Iconify icon="lucide:triangle-alert" width={32} sx={{ color: colors.iconColor }} />
        </Box>

        <Typography
          component="h1"
          sx={{ color: colors.title, fontFamily: 'inherit', fontSize: 26, fontWeight: 600, letterSpacing: '-0.02em' }}
        >
          Something went wrong
        </Typography>

        <Typography
          sx={{ mt: '14px', color: colors.body, fontFamily: 'inherit', fontSize: '14.5px', lineHeight: 1.65 }}
        >
          Sorry, an unexpected error stopped this page from loading. Try reloading
          {reference
            ? ' — if the problem persists, contact support and quote the reference below.'
            : '. If the problem persists, contact support.'}
        </Typography>

        {reference && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '9px',
              mt: '22px',
              bgcolor: colors.pillBg,
              border: `1px solid ${colors.pillBorder}`,
              borderRadius: '9px',
              pl: '14px',
              pr: '12px',
              py: '9px',
            }}
          >
            <Box component="span" sx={{ color: colors.refLabel, fontFamily: 'inherit', fontSize: 12 }}>
              Reference
            </Box>
            <Box
              component="span"
              sx={{
                color: colors.refValue,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: '12.5px',
                letterSpacing: '-0.01em',
              }}
            >
              {reference}
            </Box>
            <ButtonBase
              onClick={copyReference}
              aria-label="Copy reference"
              title="Copy reference"
              sx={{
                width: 28,
                height: 28,
                flex: 'none',
                borderRadius: '7px',
                color: colors.copyColor,
                '&:hover': { bgcolor: colors.copyHoverBg, color: colors.copyHoverColor },
              }}
            >
              <Iconify icon={copied ? 'lucide:check' : 'lucide:copy'} width={15} />
            </ButtonBase>
          </Box>
        )}

        <Stack direction="row" sx={{ mt: '30px', gap: '12px', alignItems: 'center' }}>
          <ButtonBase
            onClick={() => window.location.reload()}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              bgcolor: colors.reloadBg,
              color: colors.reloadColor,
              border: `1px solid ${colors.reloadBorder}`,
              borderRadius: '9px',
              px: '18px',
              py: '11px',
              fontFamily: 'inherit',
              fontSize: '13.5px',
              fontWeight: 500,
              '&:hover': { bgcolor: colors.reloadHoverBg, borderColor: colors.reloadHoverBorder },
            }}
          >
            <Iconify icon="lucide:rotate-cw" width={15} />
            Reload page
          </ButtonBase>

          <ButtonBase
            onClick={() => {
              window.location.href = '/';
            }}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              bgcolor: colors.homeBg,
              color: colors.homeColor,
              borderRadius: '9px',
              px: '18px',
              py: '11px',
              fontFamily: 'inherit',
              fontSize: '13.5px',
              fontWeight: 600,
              '&:hover': { bgcolor: colors.homeHoverBg },
            }}
          >
            <Iconify icon="lucide:house" width={15} />
            Go to Home
          </ButtonBase>
        </Stack>

        <Box
          component="a"
          href={`mailto:${SUPPORT_EMAIL}`}
          sx={{
            mt: '24px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            color: colors.linkColor,
            fontFamily: 'inherit',
            fontSize: 13,
            textDecoration: 'none',
            '&:hover': { color: colors.linkHoverColor },
          }}
        >
          <Iconify icon="lucide:life-buoy" width={14} />
          Contact support
        </Box>
      </Stack>
    </Box>
  );
};
